import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import useDebounce from "../../../hooks/useDebounce";
import { useConsultantsByCollectiveAgreement } from "../../../networking/guaranteedHours.hooks";
import LoadingIndicator from "../LoadingIndicator/LoadingIndicator";

export interface AssignedConsultantsSidebarProps {
	collectiveAgreementId: string;
}

const AssignedConsultantsSidebar: React.FC<AssignedConsultantsSidebarProps> = ({
	collectiveAgreementId,
}) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [searchTerm, setSearchTerm] = useState("");
	const debouncedSearchTerm = useDebounce(searchTerm, 300);

	const { data, isLoading } = useConsultantsByCollectiveAgreement(collectiveAgreementId);

	const filteredConsultants = useMemo(() => {
		if (!data?.consultants) return [];

		if (!debouncedSearchTerm) return data.consultants;

		const lowerSearchTerm = debouncedSearchTerm.toLowerCase();
		return data.consultants.filter((consultant) => {
			const fullName = `${consultant.firstName} ${consultant.lastName}`.toLowerCase();
			const employeeNumber = consultant.employeeNumber?.toString() || "";
			return fullName.includes(lowerSearchTerm) || employeeNumber.includes(debouncedSearchTerm);
		});
	}, [data?.consultants, debouncedSearchTerm]);

	const handleConsultantClick = (consultantId: string) => {
		navigate(`/consultants/${consultantId}/employment-and-salary`);
	};

	return (
		<div className="flex flex-col h-screen">
			{/* Header Section */}
			<div className="flex-shrink-0 bg-gray-100 p-4 space-y-3 border-b border-gray-300">
				<h2 className="text-xl font-bold">{t("collectiveAgreement.assignedConsultants")}</h2>

				<p className="text-sm text-gray-600">{data?.count || 0} konsulter</p>

				{/* Search Input */}
				<div className="relative">
					<div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
						<svg
							className="w-4 h-4 text-gray-400"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth={2}
								d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
							/>
						</svg>
					</div>
					<input
						type="text"
						placeholder={t("SEARCH")}
						value={searchTerm}
						onChange={(e) => setSearchTerm(e.target.value)}
						className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
					/>
				</div>
			</div>

			{/* Consultants List */}
			<div className="flex-1 overflow-y-auto p-4">
				{isLoading ? (
					<div className="flex justify-center items-center h-[200px]">
						<LoadingIndicator />
					</div>
				) : filteredConsultants.length === 0 ? (
					<p className="text-gray-500 text-center mt-8">
						{debouncedSearchTerm
							? t("NO_SEARCH_RESULTS")
							: t("collectiveAgreement.noAssignedConsultants")}
					</p>
				) : (
					<div className="flex flex-col space-y-2">
						{filteredConsultants.map((consultant) => (
							<div
								key={consultant.id}
								className="p-3 border border-gray-200 rounded-md hover:bg-gray-50 hover:border-gray-400 cursor-pointer transition-colors"
								onClick={() => handleConsultantClick(consultant.id)}
							>
								<div className="flex justify-between items-start">
									<div className="flex flex-col space-y-1">
										<p className="font-semibold text-gray-600">
											{consultant.firstName} {consultant.lastName}
										</p>
										{consultant.employeeNumber && (
											<p className="text-sm text-gray-600">
												{t("consultant.employeeNumber")}: {consultant.employeeNumber}
											</p>
										)}
									</div>
								</div>
							</div>
						))}
					</div>
				)}
			</div>
		</div>
	);
};

export default AssignedConsultantsSidebar;
